/**
 * EngagementReadService.js
 * 
 * Service for reading engagement data from Firestore.
 */

// Firebase
import { collection, getDocs, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Constants
const ENGAGEMENTS_COLLECTION = collections.engagements;

/**
 * Subscribes to engagements using the same (params, sortFields) shape as the legacy subscription pattern.
 *
 * @param {{ params: Array<{ field: string, operator: any, value: any }>, sortFields: Array<string|{ field: string, order: 'asc'|'desc' }> }} options
 * @param {function(Array<any>): void} callback - Callback to handle engagement data updates
 * 
 * @returns {function(): void} Unsubscribe function
 */
export function subscribeToEngagements({ params = [], sortFields = [] } = {}, callback) {

  // Validate callback
  if (typeof callback !== 'function') {
    return () => { };
  }

  // Build query constraints
  const constraints = [];

  // Apply filters
  (params || []).forEach((param) => {
    if (param?.field && param?.operator && param?.value !== undefined) {
      constraints.push(where(param.field, param.operator, param.value));
    }
  });

  // Apply sorting
  (sortFields || []).forEach((field) => {
    if (typeof field === 'string' && field) {
      constraints.push(orderBy(field));
      return;
    }
    if (field?.field && field?.order) {
      constraints.push(orderBy(field.field, field.order));
    }
  });

  // Create query
  const q = query(collection(db, ENGAGEMENTS_COLLECTION), ...constraints);

  // Subscribe to real-time updates
  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
      }));
      callback(rows);
    },
    (error) => {
      console.error('[EngagementReadService] subscribeToEngagements error:', error);
      callback([]);
    }
  );
}

/**
 * Lists engagement item summaries for a given user and engagement type.
 * Intended to keep UI/hooks free of direct Firestore access.
 * 
 * @param {{ userKey: string, type: string }} options - Options containing userKey and engagement type.
 * 
 * @returns {Promise<Array<any>>} - Promise resolving to an array of engagement item summaries.
 */
export async function listEngagementSummariesByType({ userKey, type }) {

  // Validate inputs
  if (!userKey || !type) return [];

  // Create query
  const engagementsRef = collection(db, ENGAGEMENTS_COLLECTION);

  // Query for engagements matching userKey and type
  const q = query(
    engagementsRef,
    where('userKey', '==', userKey),
    where('type', '==', type)
  );

  // Execute query
  const snapshot = await getDocs(q);

  // Extract and return item summaries
  return snapshot.docs
    .map((docSnap) => (docSnap.data() || {}).itemSummary)
    .filter(Boolean);
}
